import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { APP_VERSION } from '@/appVersion';
import AdminSidebarNavItem from '@/components/layout/admin/AdminSidebarNavItem';

export default function AdminSidebar({
  adminNavGroups,
  sidebarCollapsed,
  toggleCollapse,
  addonPendingCount,
  auditNewCount,
  sidebarOpen,
  setSidebarOpen,
}) {
  return (
    <>
      <aside className={cn('hidden md:flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transition-all duration-300 relative', sidebarCollapsed ? 'w-16' : 'w-56')}>
        <div className={cn('shrink-0 min-h-[7.5rem] py-2.5 flex items-center border-b border-slate-200 dark:border-slate-800 transition-all duration-300', sidebarCollapsed ? 'justify-center px-2' : 'px-3')}>
          <button
            type="button"
            onClick={toggleCollapse}
            className={cn(
              'flex rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 min-w-0 hover:opacity-90 transition-opacity',
              sidebarCollapsed ? 'justify-center p-0.5' : 'w-full justify-start',
            )}
            aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
            aria-expanded={!sidebarCollapsed}
            data-testid="sidebar-logo-toggle"
          >
            <img
              src="/logo.svg"
              alt=""
              className={cn(
                'object-contain object-left shrink-0 min-w-0 pointer-events-none',
                sidebarCollapsed ? 'h-11 w-11 max-h-11 max-w-[2.75rem]' : 'h-[52px] w-auto max-h-[52px] max-w-full',
              )}
              style={{ objectFit: 'contain' }}
            />
          </button>
        </div>
        <ScrollArea className="flex-1 py-3">
          <nav className={cn(sidebarCollapsed ? 'px-2' : 'px-3')}>
            {adminNavGroups.map((group, gi) => (
              <div key={group.label ?? 'top'}>
                {gi > 0 ? <div className="border-t border-slate-200/50 dark:border-slate-800/50 my-1" /> : null}
                {group.label && !sidebarCollapsed ? <div className="text-[9px] text-muted-foreground/60 font-semibold uppercase tracking-wider px-3 pt-3 pb-1">{group.label}</div> : null}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <AdminSidebarNavItem key={item.path} item={item} sidebarCollapsed={sidebarCollapsed} addonPendingCount={addonPendingCount} auditNewCount={auditNewCount} />
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </ScrollArea>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 shrink-0">
          <p className="text-xs text-muted-foreground/60 text-center" data-testid="admin-sidebar-version">
            LotMate v{APP_VERSION}
          </p>
        </div>
      </aside>
      {sidebarOpen ? <div className="fixed inset-0 bg-black/50 dark:bg-black/60 z-40 md:hidden" onClick={() => setSidebarOpen(false)} /> : null}
      <aside className={cn('fixed inset-y-0 left-0 w-56 max-w-[85vw] flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 z-50 transform transition-transform md:hidden', sidebarOpen ? 'translate-x-0' : '-translate-x-full')}>
        <div className="shrink-0 min-h-[7.5rem] py-2.5 flex items-center justify-between gap-2 px-3 border-b border-slate-200 dark:border-slate-800">
          <img
            src="/logo.svg"
            alt="LotMate"
            className="shrink-0 h-[52px] w-auto max-h-[52px] max-w-[min(100%,14rem)] object-contain"
          />
          <button type="button" onClick={() => setSidebarOpen(false)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 shrink-0" aria-label="Close menu">
            <X size={20} />
          </button>
        </div>
        <ScrollArea className="flex-1 py-3">
          <nav className="px-3">
            {adminNavGroups.map((group, gi) => (
              <div key={group.label ?? 'top'}>
                {gi > 0 ? <div className="border-t border-slate-200/50 dark:border-slate-800/50 my-1" /> : null}
                {group.label ? <div className="text-[9px] text-muted-foreground/60 font-semibold uppercase tracking-wider px-3 pt-3 pb-1">{group.label}</div> : null}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <AdminSidebarNavItem key={item.path} item={item} addonPendingCount={addonPendingCount} auditNewCount={auditNewCount} onClick={() => setSidebarOpen(false)} mobile />
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </ScrollArea>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 shrink-0">
          <p className="text-xs text-muted-foreground/60 text-center">LotMate v{APP_VERSION}</p>
        </div>
      </aside>
    </>
  );
}
